import React, { useState, useEffect } from 'react';
import { Drawer, DrawerContent, DrawerNavigation } from '@progress/kendo-react-layout';
import { FaTimesCircle } from 'react-icons/fa';
import GroupsGrid from '../../components/grids/GroupsGrid';
import AddGroupForm from '../../components/FormComps/Groups/AddGroupForm';
import EditGroupDetails from '../../components/FormComps/Groups/EditGroupDetails';
import { getGroups, createGroup, Group } from '../../data/groupData';
import { useToast } from '../../contexts/ToastContext';
import { canEditArea, isAdministrator as hasAdministrator } from '../../helpers/areaAccess';
import { getApiErrorDetails } from '../../helpers/apiError';

interface GroupsProps {
  allowedRoutes?: string[];
}

function Groups(props: GroupsProps) {
  // Groups.Manage or an administrator (see areaAccess.ts). Only an administrator may touch a group that grants
  // Portal.Administrator - the server enforces both.
  const canEdit = canEditArea(props.allowedRoutes, 'Groups');
  const isAdministrator = hasAdministrator(props.allowedRoutes);
  const { showToast } = useToast();
  const [groupsData, setGroupsData] = useState<Group[]>([]);
  const [expanded, setExpanded] = useState<boolean>(false);
  const [selectedGroupId, setSelectedGroupId] = useState<number | null>(null);
  const [addGroupVisible, setAddGroupVisible] = useState<boolean>(false);

  useEffect(() => {
    loadGroupsData();
  }, []);

  const loadGroupsData = async () => {
    try {
      const data = await getGroups();
      setGroupsData(data);
    } catch (error: any) {
      console.error('Error loading groups:', error);
      showToast('Error loading groups', 'error');
    }
  };

  const handleAddGroup = () => {
    setAddGroupVisible(true);
    setSelectedGroupId(null);
    setExpanded(true);
  };

  const handleEditGroup = (group: Group) => {
    setSelectedGroupId(group.groupId);
    setAddGroupVisible(false);
    setExpanded(true);
  };

  const newGroupHandler = async (data: any) => {
    try {
      await createGroup(data);
      showToast('Group created successfully', 'success');
      setAddGroupVisible(false);
      setExpanded(false);
      loadGroupsData();
    } catch (error: any) {
      console.error('Error creating group:', error);
      showToast(getApiErrorDetails(error, 'Error creating group').message, 'error');
    }
  };

  const handleGroupUpdate = () => {
    loadGroupsData();
  };

  const handleExpanded = (value: boolean) => {
    setExpanded(value);
    if (!value) {
      setSelectedGroupId(null);
      setAddGroupVisible(false);
    }
  };

  return (
    <div>
      <Drawer
        expanded={expanded}
        position={'end'}
        width={1000}
        mode='overlay'
        animation={{
          duration: 400,
        }}
        onOverlayClick={() => handleExpanded(false)}
      >
        <DrawerContent>
          <DrawerNavigation>
            {addGroupVisible && canEdit && (
              <>
                <div className='form__title__container'>
                  <h1 className='form__title'>Add Group</h1>
                  <FaTimesCircle
                    className='closeform'
                    onClick={() => handleExpanded(false)}
                  />
                </div>
                <div className='form__wrapper'>
                  <AddGroupForm
                    canGrantAdmin={isAdministrator}
                    onSubmit={newGroupHandler}
                    onCancel={() => handleExpanded(false)}
                  />
                </div>
              </>
            )}
            {selectedGroupId && !addGroupVisible && (
              <>
                <div className='form__title__container'>
                  <h1 className='form__title'>{canEdit ? 'Edit Group' : 'Group'}</h1>
                  <FaTimesCircle
                    className='closeform'
                    onClick={() => handleExpanded(false)}
                  />
                </div>
                <div className='form__wrapper'>
                  <EditGroupDetails
                    groupId={selectedGroupId}
                    canEdit={canEdit}
                    isAdministrator={isAdministrator}
                    onUpdate={handleGroupUpdate}
                    onClose={() => handleExpanded(false)}
                  />
                </div>
              </>
            )}
          </DrawerNavigation>
          <GroupsGrid
            data={groupsData}
            onAdd={canEdit ? handleAddGroup : undefined}
            onEdit={handleEditGroup}
          />
        </DrawerContent>
      </Drawer>
    </div>
  );
}

export default Groups;
